import React, { useEffect, useState } from "react";
import { HeartPulse, AlertTriangle, Info, ShieldAlert } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SEV = {
  critical: { color: "#FF6B57", Icon: ShieldAlert },
  warning: { color: "#FFB020", Icon: AlertTriangle },
  info: { color: "#00E5FF", Icon: Info },
};

/** Ford vehicle-health alert feed (/v1/vehicle-health/alerts) as in-app
 *  warning cards. Renders only for connected Ford sessions. */
export const FordHealthAlertsCard = ({ vehicle }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const sessionId =
    typeof window !== "undefined" ? window.localStorage.getItem("eviq.tesla_session_id") : null;
  const isFordSession = !!sessionId && sessionId.startsWith("ford-");

  useEffect(() => {
    if (!isFordSession || !API) return;
    fetch(`${API}/api/ford/health_alerts?session_id=${encodeURIComponent(sessionId)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setError(true));
  }, [sessionId, isFordSession]);

  if (vehicle?.brand !== "ford" || !isFordSession) return null;

  const alerts = data?.alerts || [];

  return (
    <div
      className="rounded-xl border p-4 mt-3"
      style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}
      data-testid="ford-health-alerts-card"
    >
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center border shrink-0"
          style={{ background: "rgba(57,255,20,0.08)", borderColor: "rgba(57,255,20,0.30)" }}
        >
          <HeartPulse className="w-4 h-4" style={{ color: "#39FF14" }} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
            Ford vehicle health
          </div>
          <div className="text-sm text-white font-semibold leading-snug">
            {error ? "Health feed unavailable" : data === null ? "Checking health alerts…" : alerts.length ? `${alerts.length} active alert${alerts.length > 1 ? "s" : ""}` : "No active health alerts"}
          </div>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="ford-health-alerts-list">
          {alerts.map((a) => {
            const sev = SEV[a.severity] || SEV.info;
            return (
              <div
                key={a.id}
                className="rounded-lg border p-3 flex gap-2.5"
                style={{ borderColor: `${sev.color}40`, background: `${sev.color}0d` }}
                data-testid={`ford-health-alert-${a.id}`}
              >
                <sev.Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: sev.color }} strokeWidth={2.4} />
                <div className="min-w-0">
                  <div className="text-[12px] text-white font-semibold leading-snug">{a.title}</div>
                  {a.description ? (
                    <p className="font-mono text-[10px] text-zinc-400 leading-relaxed mt-1">{a.description}</p>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data?.demo && (
        <p className="font-mono text-[9px] text-zinc-600 mt-2 uppercase tracking-wider">
          Demo data — sample alerts for preview
        </p>
      )}
    </div>
  );
};
